import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { api, type ApiResponse } from "~/lib/api";
import { periodWindow, toPeriod } from "~/lib/period";
import { BandScale, Columns, Figure, Ledger, RankedBars } from "~/components/charts";
import { ActionItems } from "./ActionItems";
import { DORA_BANDS, healthDek, healthHeadline, ledgerChange, type Band } from "../story";
import { fmtHoursFixed, fmtFixed, periodName, priorName, fmtDay } from "~/lib/format";
import { ed } from "~/components/editorial";

interface WorkDistributionItem {
  category: string;
  prCount: number;
  percentage: number;
}

interface ActionItemAuthor {
  id: string;
  name: string;
  avatarUrl: string | null;
}

interface ActionItemPR {
  id: string;
  title: string;
  number: number;
  author: ActionItemAuthor | null;
  url: string;
  daysOpen?: number;
  hoursWaiting?: number;
  linesChanged?: number;
  daysSinceRequest?: number;
}

interface MetricWithTrend {
  value: number;
  previousValue: number;
  changePercent: number;
}

interface CodeHealthResponse {
  metrics: {
    prsMerged: MetricWithTrend;
    avgTimeToMerge: MetricWithTrend;
    avgTimeToFirstReview: MetricWithTrend;
    openPrs: number;
    commits: MetricWithTrend;
    linesChanged: {
      additions: number;
      deletions: number;
    };
  };
  actionItems: {
    stalePrs: ActionItemPR[];
    waitingForReview: ActionItemPR[];
    largePrs: ActionItemPR[];
    changesRequested: ActionItemPR[];
  };
}

interface CodeHealthDashboardProps {
  range: string;
}

interface DORAData {
  deploymentFrequency: {
    value: number;
    total: number;
    band: Band;
    bandLabel: string;
  };
  leadTimeForChanges: {
    value: number;
    median: number;
    band: Band;
    bandLabel: string;
  };
  benchmarks?: {
    deploymentFrequency: Record<Band, string>;
    leadTime: Record<Band, string>;
  };
}

export function CodeHealthDashboard({ range }: CodeHealthDashboardProps) {
  const [searchParams] = useSearchParams();
  const win = periodWindow(toPeriod(range));
  const now = periodName(win.label);
  const prior = priorName(win.prevLabel);

  const { data, isLoading, error } = useQuery({
    queryKey: ["code-health", range],
    queryFn: () => api.get<CodeHealthResponse>(`/code/health?range=${range}`),
    staleTime: 60 * 1000,
  });

  const { data: throughputData } = useQuery({
    queryKey: ["code-throughput", range],
    queryFn: () =>
      api.get<{
        data: Array<{ date: string; prsMerged: number; commits: number }>;
      }>(`/code/throughput?range=${range}`),
    staleTime: 60 * 1000,
  });

  const { data: doraData } = useQuery({
    queryKey: ["dora-metrics", range],
    queryFn: () =>
      api.get<ApiResponse<DORAData>>(`/metrics/dora?range=${range}`),
    staleTime: 5 * 60 * 1000,
  });

  const { data: workDistData } = useQuery({
    queryKey: ["code-work-distribution", range],
    queryFn: () =>
      api.get<{ data: WorkDistributionItem[] }>(
        `/code/work-distribution?range=${range}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <div className={ed.loading}>Loading code health…</div>;
  if (error)
    return <div className={ed.loading}>Couldn't load code health data.</div>;
  if (!data) return <div className={ed.loading}>No code health data yet.</div>;

  const { metrics, actionItems } = data;
  const throughput = throughputData?.data ?? [];
  const work = [...(workDistData?.data ?? [])].sort(
    (a, b) => b.prCount - a.prCount,
  );
  const workTotal = work.reduce((s, w) => s + w.prCount, 0);
  const dora = doraData?.data;

  const story = {
    now,
    prior,
    merged: metrics.prsMerged.value,
    mergedPrev: metrics.prsMerged.previousValue,
    mergedChangePct: metrics.prsMerged.changePercent,
    avgMergeHours: metrics.avgTimeToMerge.value,
    avgMergeHoursPrev: metrics.avgTimeToMerge.previousValue,
    attention: {
      changes: actionItems.changesRequested.length,
      stale: actionItems.stalePrs.length,
      waiting: actionItems.waitingForReview.length,
      large: actionItems.largePrs.length,
    },
  };
  const attentionTotal =
    story.attention.changes +
    story.attention.stale +
    story.attention.waiting +
    story.attention.large;

  const peakMerged = throughput.reduce<{ date: string; v: number } | null>(
    (best, d) =>
      d.prsMerged > (best?.v ?? 0) ? { date: d.date, v: d.prsMerged } : best,
    null,
  );
  const peakCommits = throughput.reduce<{ date: string; v: number } | null>(
    (best, d) =>
      d.commits > (best?.v ?? 0) ? { date: d.date, v: d.commits } : best,
    null,
  );
  const lead = work[0];
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";

  return (
    <>
      <div className={ed.kicker}>Code health · {now}</div>
      <h1 className={ed.headlineCompact}>{healthHeadline(story)}</h1>
      <p className={ed.dek}>{healthDek(story)}</p>
      <div className={ed.byline}>
        <b>Code health</b> · merged PRs and commits, {win.label}, compared with{" "}
        {win.prevLabel}. Open PRs and the lists below are as of now. From
        GitHub.
      </div>

      <Ledger
        hideMissingPrior
        columns="fit"
        items={[
          {
            label: "PRs merged",
            value: fmtFixed(metrics.prsMerged.value),
            prior: fmtFixed(metrics.prsMerged.previousValue),
            ...ledgerChange(
              metrics.prsMerged.value,
              metrics.prsMerged.previousValue,
            ),
          },
          {
            label: "Open to merge",
            value: fmtHoursFixed(metrics.avgTimeToMerge.value),
            prior: fmtHoursFixed(metrics.avgTimeToMerge.previousValue),
            note: "average",
            ...ledgerChange(
              metrics.avgTimeToMerge.value,
              metrics.avgTimeToMerge.previousValue,
              1,
              "h",
            ),
          },
          {
            label: "Wait for review",
            value: fmtHoursFixed(metrics.avgTimeToFirstReview.value),
            prior: fmtHoursFixed(metrics.avgTimeToFirstReview.previousValue),
            note: "average, first review",
            ...ledgerChange(
              metrics.avgTimeToFirstReview.value,
              metrics.avgTimeToFirstReview.previousValue,
              1,
              "h",
            ),
          },
          {
            label: "Commits",
            value: fmtFixed(metrics.commits.value),
            prior: fmtFixed(metrics.commits.previousValue),
            ...ledgerChange(
              metrics.commits.value,
              metrics.commits.previousValue,
            ),
          },
          {
            label: "Open PRs",
            value: fmtFixed(metrics.openPrs),
            note: "right now",
          },
          {
            label: "Lines changed",
            value: fmtFixed(
              metrics.linesChanged.additions + metrics.linesChanged.deletions,
            ),
            note: `+${fmtFixed(metrics.linesChanged.additions)} −${fmtFixed(metrics.linesChanged.deletions)}`,
          },
        ]}
      />

      <div className={ed.sect}>I. Needs attention</div>
      <h2 className={ed.h2}>
        {attentionTotal === 0
          ? "Nothing open is waiting on anyone"
          : `${fmtFixed(attentionTotal)} ${attentionTotal === 1 ? "flag" : "flags"} on open PRs`}
      </h2>
      <p className={ed.proseLinked}>
        Grouped by what's holding them up, most urgent first. A PR can appear in
        more than one group. Titles open the PR on GitHub.
      </p>
      <ActionItems
        stalePrs={actionItems.stalePrs}
        waitingForReview={actionItems.waitingForReview}
        largePrs={actionItems.largePrs}
        changesRequested={actionItems.changesRequested}
      />

      <div className={ed.sect}>II. Throughput</div>
      <h2 className={ed.h2}>
        {peakMerged
          ? `Merges peaked on ${fmtDay(peakMerged.date)} with ${fmtFixed(peakMerged.v)}`
          : `No merges yet in ${now}`}
      </h2>
      <div className={ed.halves}>
        <Figure
          title="Merged PRs per day"
          sub={peakMerged ? "Busiest day in color." : undefined}
          method="PRs merged on each calendar day of the window, all repositories, bots included."
          table={{
            columns: ["Day", "Merged"],
            rows: throughput.map((d) => [fmtDay(d.date), d.prsMerged]),
          }}
        >
          {throughput.length > 0 ? (
            <Columns
              points={throughput.map((d) => ({
                key: d.date,
                label: fmtDay(d.date),
                value: d.prsMerged,
              }))}
              ariaLabel="Merged PRs per day"
              unit="merged"
              emphasizeMax
              width={560}
            />
          ) : (
            <div className={ed.loading}>No data yet.</div>
          )}
        </Figure>
        <Figure
          title="Commits per day"
          sub={
            peakCommits
              ? `Peak ${fmtFixed(peakCommits.v)} on ${fmtDay(peakCommits.date)}, in color.`
              : undefined
          }
          method="Commits by commit date across synced repositories, including commits on unmerged branches."
          table={{
            columns: ["Day", "Commits"],
            rows: throughput.map((d) => [fmtDay(d.date), d.commits]),
          }}
        >
          {throughput.length > 0 ? (
            <Columns
              points={throughput.map((d) => ({
                key: d.date,
                label: fmtDay(d.date),
                value: d.commits,
              }))}
              ariaLabel="Commits per day"
              unit="commits"
              emphasizeMax
              width={560}
            />
          ) : (
            <div className={ed.loading}>No data yet.</div>
          )}
        </Figure>
      </div>

      {dora && (
        <>
          <div className={ed.sect}>III. Delivery</div>
          <h2 className={ed.h2}>
            {fmtFixed(dora.deploymentFrequency.value, 1)} deploys a week, a{" "}
            {fmtHoursFixed(dora.leadTimeForChanges.median)} median lead time
          </h2>
          <BandScale
            bands={DORA_BANDS}
            rows={[
              {
                key: "df",
                label: "Deployment frequency",
                value: `${fmtFixed(dora.deploymentFrequency.value, 1)}/wk`,
                band: dora.deploymentFrequency.band,
                thresholds: dora.benchmarks?.deploymentFrequency,
              },
              {
                key: "lt",
                label: "Lead time for changes",
                value: fmtHoursFixed(dora.leadTimeForChanges.median),
                note: "median",
                band: dora.leadTimeForChanges.band,
                thresholds: dora.benchmarks?.leadTime,
              },
            ]}
          />
          <p className={ed.methodNarrow}>
            Deploys are merges to the default branch of repositories marked
            deploy-on-merge. The filled cell is where this period falls.{" "}
            <Link to={`/code/dora${suffix}`}>All four DORA metrics</Link>.
          </p>
        </>
      )}

      {work.length > 0 && lead && (
        <>
          <div className={ed.sect}>{dora ? "IV" : "III"}. The work</div>
          <h2 className={ed.h2}>
            {lead.category} made up {lead.percentage}% of merged PRs
          </h2>
          <Figure
            title="Merged PRs by kind of work"
            sub="The largest category in color."
            method={`Category from the linked JIRA issue type, else the branch and title (feature, fix, refactor, docs, release, infra). ${fmtFixed(workTotal)} merged PRs.`}
            table={{
              columns: ["Category", "PRs", "Share"],
              rows: work.map((w) => [
                w.category,
                w.prCount,
                `${w.percentage}%`,
              ]),
            }}
          >
            <RankedBars
              rows={work.map((w, i) => ({
                id: w.category,
                label: w.category,
                value: w.prCount,
                emphasis: i === 0,
              }))}
              ariaLabel="Merged PRs by category"
            />
          </Figure>
        </>
      )}
    </>
  );
}
